import { FacilityId, FavoriteFacility } from '@gyym/sdk'
import { useGyymClient } from '@gyym/sdk-react'
import { useSnackbar } from '@gyym/ui'
import React from 'react'

import { useAuthUser, useResendVerificationEmail } from '@/modules/Auth'

import { useFavoritedFacilities } from './useFavoritedFacilities'

export const useToggleFavorite = (options?: {
  target: string
  enabled?: boolean
}) => {
  const client = useGyymClient()
  const { target, enabled = true } = options || {}
  const { isLoggedIn, isEmailVerified } = useAuthUser()
  const resendVerificationEmail = useResendVerificationEmail(
    // TODO: Temporarily set intended return path to TOP
    // Should replace to the current path after migrating all pages to app router using next/navigation
    '/',
  )

  const { data: favoritedFacilities, mutate } = useFavoritedFacilities(
    undefined,
    enabled,
    { revalidateOnMount: true },
  )
  const { openSnackbar } = useSnackbar()

  const favoritedFacilityIds = React.useMemo(
    () =>
      favoritedFacilities
        ? favoritedFacilities.map((facility) => facility.id)
        : [],
    [favoritedFacilities],
  )

  const isFavoriteLoading =
    isLoggedIn && isEmailVerified ? !Array.isArray(favoritedFacilities) : false

  const isFavorited = React.useCallback(
    (id: FacilityId) => {
      return favoritedFacilityIds.includes(id)
    },
    [favoritedFacilityIds],
  )

  const onToggle = React.useCallback(
    async (id: FacilityId) => {
      // User is logged out
      if (!isLoggedIn) {
        openSnackbar({
          message: 'お気に入り登録にはログインが必要です',
          action: {
            label: 'ログインする',
            href: '/login',
            target,
          },
        })
        return
      }

      // User has not verified email
      if (!isEmailVerified) {
        openSnackbar({
          message: 'お気に入り登録にはメールアドレスの認証が必要です',
          action: {
            label: 'メールを再送',
            onClick: resendVerificationEmail,
          },
        })
        return
      }

      const isFacilityFavorited = isFavorited(id)

      // Facility is not favorited
      if (!isFacilityFavorited) {
        // Add facility to favorites

        // Add facility to favorites optimistically
        // NOTE: Update only the id.
        // This could be a problem when adding a facility to favorites inside a favorite list,
        // since it doesn't have any images, station, etc.
        const fakeFacility = { id } as FavoriteFacility
        mutate((v) => (v ? [...v, fakeFacility] : [fakeFacility]), false)

        try {
          await client.user.favorites.addFacility(id)
          openSnackbar({
            variant: 'success',
            message: 'お気に入り登録しました',
          })
        } catch {
          // TODO: Error handling

          // Revalidate to fresh data
          mutate((v) => v, true)

          openSnackbar({
            variant: 'error',
            message: 'お気に入り登録できませんでした',
          })
        }
      }

      // Facility is already favorited
      if (isFacilityFavorited) {
        // Remove facility from favorites

        // Filter out the facility optimistically
        mutate((v) => v?.filter(({ id: fId }) => fId !== id), false)

        try {
          await client.user.favorites.removeFacility(id)

          openSnackbar({
            message: 'お気に入り解除しました',
          })
        } catch {
          // TODO: Error handling

          // Revalidate to fresh data
          mutate((v) => v, true)

          openSnackbar({
            variant: 'error',
            message: 'お気に入り解除できませんでした',
          })
        }
      }
    },
    [
      client,
      isLoggedIn,
      isEmailVerified,
      mutate,
      openSnackbar,
      target,
      isFavorited,
      resendVerificationEmail,
    ],
  )

  return {
    isFavorited,
    onToggle,
    favoritedFacilityIds,
    // Flag to check if the facility is ready to be favorited or un-favorited
    isFavoriteLoading,
  }
}
